import { Database, DatabaseMetadata, DatabaseEntry, DatabaseFilters, ProjectInfo, SourceInfo, DatabaseStats } from '../../../shared/types';

/**
 * Einheitlicher Database Service für die datenbankzentrierte Architektur v2.0
 * Verwaltet alle Datenbanken mit professioneller Namenskonvention und Metadaten
 */
export class DatabaseService {
  private baseUrl = '/api/databases';

  /**
   * Generiert professionellen Datenbanknamen nach Konvention:
   * "YYYY.MM.DD - [Projekt/Angebotsnummer] ([Ort/Straße])"
   */
  generateDatabaseName(projectInfo: ProjectInfo): string {
    const date = projectInfo.date || new Date().toISOString().split('T')[0];
    const formattedDate = date.replace(/-/g, '.');
    const projectPart = projectInfo.projectNumber || 'Projekt';
    const detailPart = projectInfo.customerName || projectInfo.location;
    const locationPart = detailPart ? ` (${detailPart})` : '';
    
    return `${formattedDate} - ${projectPart}${locationPart}`;
  }

  /**
   * Parst Datenbanknamen zurück zu ProjectInfo
   */
  parseDatabaseName(name: string): ProjectInfo {
    const match = name.match(/^(\d{4}\.\d{2}\.\d{2}) - ([^(]+)(?:\(([^)]+)\))?$/);
    if (!match) return { date: new Date().toISOString().split('T')[0] };
    
    return {
      date: match[1].replace(/\./g, '-'),
      projectNumber: match[2].trim(),
      customerName: match[3]?.trim(),
      location: match[3]?.trim()
    };
  }

  /**
   * Validiert Namenskonvention
   */
  validateNaming(name: string): boolean {
    return /^\d{4}\.\d{2}\.\d{2} - .+/.test(name);
  }

  /**
   * Berechnet Statistiken für Datenbankeinträge
   */
  calculateStats(entries: DatabaseEntry[]): DatabaseStats {
    const entryCount = entries.length;
    const totalValue = entries.reduce((sum, entry) => {
      // Bedarfs-/Alternativpositionen zählen nicht in die (Haupt-)Summe der DB-Liste.
      if ((entry as any).isBedarfsposition || (entry as any).isAlternativposition) return sum;
      const price = parseFloat(entry.einzelpreis?.toString() || entry.preis?.toString() || '0');
      const quantity = parseFloat(entry.menge?.toString() || '1');
      return sum + (price * quantity);
    }, 0);
    
    const gewerke = new Set(entries.map(entry => entry.gewerk).filter(Boolean));
    const gewerkCount = gewerke.size;

    return {
      entryCount,
      totalValue: totalValue > 0 ? totalValue : undefined,
      gewerkCount: gewerkCount > 0 ? gewerkCount : undefined
    };
  }

  /**
   * Prüft ob eine Datenbank Mengen enthält (Angebot) oder nicht (reine Datenbank)
   */
  hasQuantities(entries: DatabaseEntry[]): boolean {
    return entries.some(entry => entry.menge !== undefined && entry.menge > 0);
  }

  /**
   * Erstellt eine leere Datenbank-Kopie aus einem Angebot (ohne Mengen)
   */
  createEmptyDatabaseCopy(database: Database, newProjectInfo?: Partial<ProjectInfo>): Database {
    const projectInfo = {
      ...database.projectInfo,
      ...newProjectInfo,
      projectNumber: newProjectInfo?.projectNumber || `${database.projectInfo.projectNumber}-DB`
    };

    // Entferne alle Mengen aus den Einträgen
    const entriesWithoutQuantities: DatabaseEntry[] = database.entries.map(entry => {
      const entryWithoutQuantities = {
        ...entry,
      } as DatabaseEntry & Record<string, unknown>;
      delete entryWithoutQuantities.menge;
      delete entryWithoutQuantities.adjustedQuantity;
      delete entryWithoutQuantities.originalQuantity;
      return entryWithoutQuantities;
    });

    const newDatabase: Database = {
      ...database,
      id: this.generateId(),
      name: this.generateDatabaseName(projectInfo),
      type: 'manual', // Wird zu einer manuellen Datenbank
      projectInfo,
      entries: entriesWithoutQuantities,
      stats: this.calculateStats(entriesWithoutQuantities),
      hasQuantities: false,
      source: {
        createdBy: 'manual',
        originalFilename: `Kopie von ${database.name}`
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return newDatabase;
  }

  /**
   * Bestimmt den Anzeigenamen basierend auf Typ und Inhalt
   */
  getDisplayName(database: DatabaseMetadata): string {
    const hasQuantities = database.hasQuantities || database.type === 'quote';
    const prefix = hasQuantities ? 'Angebot:' : 'Datenbank:';
    return `${prefix} ${database.name}`;
  }

  /**
   * Bestimmt die Farbcodierung basierend auf Typ
   */
  getTypeColorInfo(type: string, hasQuantities?: boolean) {
    if (hasQuantities || type === 'quote') {
      return {
        className: 'border-orange-200 bg-orange-50 hover:bg-orange-100',
        iconColor: 'text-orange-600',
        label: 'Angebot (mit Mengen)',
        icon: '📋'
      };
    }

    switch (type) {
      case 'voice-quote':
        return {
          className: 'border-pink-200 bg-pink-50 hover:bg-pink-100',
          iconColor: 'text-pink-600',
          label: 'Voice-to-Quote KI',
          icon: '🤖'
        };
      case 'voice-created':
        return {
          className: 'border-purple-200 bg-purple-50 hover:bg-purple-100',
          iconColor: 'text-purple-600',
          label: 'Datenbank (Sprache)',
          icon: '🎤'
        };
      case 'excel-import':
        return {
          className: 'border-green-200 bg-green-50 hover:bg-green-100',
          iconColor: 'text-green-600',
          label: 'Datenbank (Excel)',
          icon: '📈'
        };
      case 'manual':
        return {
          className: 'border-blue-200 bg-blue-50 hover:bg-blue-100',
          iconColor: 'text-blue-600',
          label: 'Datenbank (Manuell)',
          icon: '✏️'
        };
      default:
        return {
          className: 'border-gray-200 bg-gray-50 hover:bg-gray-100',
          iconColor: 'text-gray-600',
          label: 'Datenbank',
          icon: '📁'
        };
    }
  }

  /**
   * Generiert eindeutige ID für neue Datenbank
   */
  private generateId(): string {
    return `db_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Erstellt neue Datenbank mit Metadaten
   */
  async createDatabase(
    projectInfo: ProjectInfo, 
    entries: DatabaseEntry[], 
    source: SourceInfo
  ): Promise<Database> {
    const name = this.generateDatabaseName(projectInfo);
    const stats = this.calculateStats(entries);
    const hasQuantities = this.hasQuantities(entries);
    
    const database: Database = {
      id: this.generateId(),
      name,
      type: source.createdBy === 'quote-builder' ? 'quote' : 
            source.createdBy === 'voice-quote-builder' ? 'voice-quote' :
            source.createdBy === 'voice-input' ? 'voice-created' : 
            source.createdBy === 'excel-import' ? 'excel-import' : 'manual',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      projectInfo,
      stats,
      source,
      status: 'active',
      hasQuantities,
      entries
    };
    
    return await this.saveDatabase(database);
  }

  /**
   * Speichert Datenbank auf Server
   */
  async saveDatabase(database: Database): Promise<Database> {
    const response = await fetch(`${this.baseUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(database),
    });

    if (!response.ok) {
      throw new Error(`Fehler beim Speichern der Datenbank: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Lädt Datenbank nach ID
   */
  async loadDatabase(id: string): Promise<Database> {
    const response = await fetch(`${this.baseUrl}/${id}`);
    
    if (!response.ok) {
      throw new Error(`Fehler beim Laden der Datenbank: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Aktualisiert bestehende Datenbank
   */
  async updateDatabase(id: string, updates: Partial<Database>): Promise<Database> {
    // Statistiken neu berechnen falls Einträge geändert wurden
    if (updates.entries) {
      updates.stats = this.calculateStats(updates.entries);
    }
    
    updates.updatedAt = new Date().toISOString();

    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Fehler beim Aktualisieren der Datenbank: ${response.statusText}`);
    }

    return await response.json();
  }

  async updateDatabaseFolders(updates: Array<{ id: string; folderId?: string | null; order?: number }>): Promise<void> {
    const response = await fetch(`${this.baseUrl}/update-folders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error(`Fehler beim Aktualisieren der Ordner: ${response.statusText}`);
    }
  }

  /**
   * Löscht Datenbank
   */
  async deleteDatabase(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Fehler beim Löschen der Datenbank: ${response.statusText}`);
    }
  }

  /**
   * Listet alle Datenbanken mit optionalen Filtern
   */
  async listDatabases(filters?: DatabaseFilters): Promise<DatabaseMetadata[]> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const url = `${this.baseUrl}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Fehler beim Laden der Datenbankliste: ${response.statusText}`);
    }

    const databases = await response.json();
    
    // Sortiere nach Datum (neueste zuerst)
    return databases.sort((a: DatabaseMetadata, b: DatabaseMetadata) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  /**
   * Konvertiert Excel-Daten in Datenbank-Format
   */
  async convertExcelToDatabase(
    excelData: DatabaseEntry[], 
    projectInfo: ProjectInfo,
    originalFilename?: string
  ): Promise<Database> {
    const source: SourceInfo = {
      createdBy: 'excel-import',
      originalFilename
    };

    return await this.createDatabase(projectInfo, excelData, source);
  }

  /**
   * Erstellt Datenbank aus Spracheingabe
   */
  async createVoiceDatabase(
    projectInfo: ProjectInfo,
    entries: DatabaseEntry[],
    transcriptions: string[]
  ): Promise<Database> {
    const source: SourceInfo = {
      createdBy: 'voice-input',
      voiceTranscriptions: transcriptions
    };

    return await this.createDatabase(projectInfo, entries, source);
  }

  /**
   * Erweitert bestehende Datenbank mit neuen Einträgen
   */
  async extendDatabase(
    id: string, 
    newEntries: DatabaseEntry[], 
    source?: Partial<SourceInfo>
  ): Promise<Database> {
    const database = await this.loadDatabase(id);
    const updatedEntries = [...database.entries, ...newEntries];
    
    const updates: Partial<Database> = {
      entries: updatedEntries,
      stats: this.calculateStats(updatedEntries)
    };

    // Füge Quellinformationen hinzu falls vorhanden
    if (source?.voiceTranscriptions) {
      updates.source = {
        ...database.source,
        voiceTranscriptions: [
          ...(database.source.voiceTranscriptions || []),
          ...source.voiceTranscriptions
        ]
      };
    }

    return await this.updateDatabase(id, updates);
  }

  /**
   * Archiviert Datenbank
   */
  async archiveDatabase(id: string): Promise<Database> {
    return await this.updateDatabase(id, { status: 'archived' });
  }

  /**
   * Aktiviert archivierte Datenbank
   */
  async activateDatabase(id: string): Promise<Database> {
    return await this.updateDatabase(id, { status: 'active' });
  }

  /**
   * Erstellt Template aus Datenbank
   */
  async createTemplate(id: string, templateName?: string): Promise<Database> {
    const database = await this.loadDatabase(id);
    
    const templateProjectInfo: ProjectInfo = {
      ...database.projectInfo,
      projectNumber: templateName || `Template-${database.projectInfo.projectNumber}`
    };

    const templateDatabase: Database = {
      ...database,
      id: this.generateId(),
      name: this.generateDatabaseName(templateProjectInfo),
      projectInfo: templateProjectInfo,
      status: 'template',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return await this.saveDatabase(templateDatabase);
  }
}

// Singleton-Instanz exportieren
export const databaseService = new DatabaseService(); 
